<template>
  <div class="retours-view">
    <section class="hero">
      <div class="container hero-grid">
        <div class="hero-left">
          <div class="hero-eyebrow">Guide complet - Retours patients</div>
          <h1 class="hero-title">Ce que vos patients<br>vous disent - et<br>comment <em>l'entendre</em></h1>
          <p class="hero-body">
            PROMs, PREMs, satisfaction, qualite de vie : comprendre les differents types de retours patients est la premiere etape pour choisir ce qui compte vraiment pour votre etablissement.
          </p>
          <a href="#contact" class="hero-cta" @click.prevent="$emit('go-home', '#contact')">Discuter de votre cas</a>
        </div>
        <div class="hero-right">
          <div class="hero-stat-card">
            <div class="stat-icon">📊</div>
            <div class="stat-text">
              <strong>4 types de retours patients</strong>
              <span>PROMs, PREMs, satisfaction, recit global - chacun mesure autre chose</span>
            </div>
          </div>
          <div class="hero-stat-card">
            <div class="stat-icon">📋</div>
            <div class="stat-text">
              <strong>Critere HAS 1.4-03 (V2025)</strong>
              <span>La mesure du vecu patient est desormais un critere avance de certification</span>
            </div>
          </div>
          <div class="hero-stat-card">
            <div class="stat-icon">💬</div>
            <div class="stat-text">
              <strong>20-35 % de taux de reponse</strong>
              <span>Avec les questionnaires papier ou email - la majorite du vecu ne vous parvient jamais</span>
            </div>
          </div>
          <div class="hero-stat-card">
            <div class="stat-icon">🎯</div>
            <div class="stat-text">
              <strong>Une conversation remplace tout</strong>
              <span>Skribi Care collecte et structure tous ces indicateurs en un seul protocole</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="taxonomy">
      <div class="container">
        <div class="taxonomy-header">
          <div>
            <div class="section-label">La taxonomie</div>
            <h2 class="section-title">Quatre facons d'ecouter<br>vos <em>patients</em></h2>
          </div>
          <p class="section-intro">
            Il n'existe pas un seul "retour patient". Chaque type de mesure repond a une question differente - et tous se completent pour donner une image complete de la qualite des soins.
          </p>
        </div>

        <div class="taxonomy-grid">
          <div class="tax-card">
            <div class="tax-number">01</div>
            <div class="tax-badge">PROMs</div>
            <div class="tax-title">Patient-Reported Outcome Measures</div>
            <div class="tax-subtitle">Le "quoi" - Resultats du soin</div>
            <p class="tax-body">
              Les PROMs mesurent l'etat de sante fonctionnel du patient <strong>du point de vue du patient lui-meme</strong>, avant et apres une intervention. Ils repondent a la question : "Le traitement a-t-il change quelque chose a votre vie quotidienne ?"
            </p>
            <ul class="tax-examples">
              <li>Oxford Knee Score - mobilite apres PTG/PTH</li>
              <li>EQ-5D - qualite de vie generale</li>
              <li>KOOS / WOMAC - fonction apres chirurgie orthopedique</li>
              <li>KCCO - insuffisance cardiaque</li>
              <li>FACT-G - oncologie, qualite de vie sous traitement</li>
            </ul>
          </div>

          <div class="tax-card">
            <div class="tax-number">02</div>
            <div class="tax-badge">PREMs</div>
            <div class="tax-title">Patient-Reported Experience Measures</div>
            <div class="tax-subtitle">Le "comment" - Experience de la prise en charge</div>
            <p class="tax-body">
              Les PREMs mesurent la <strong>qualite percue de l'interaction</strong> avec le systeme de sante : l'accueil, la communication, l'information recue, la coordination. Ils repondent a : "Comment avez-vous vecu votre passage ?"
            </p>
            <ul class="tax-examples">
              <li>Clarte des explications du chirurgien</li>
              <li>Qualite de l'information sur les suites operatoires</li>
              <li>Delai entre l'appel et la reponse infirmiere</li>
              <li>Sentiment d'avoir ete respecte et ecoute</li>
              <li>Coordination percue entre les intervenants</li>
            </ul>
          </div>

          <div class="tax-card">
            <div class="tax-number">03</div>
            <div class="tax-badge">Satisfaction</div>
            <div class="tax-title">Mesure de satisfaction globale</div>
            <div class="tax-subtitle">Le "bien/mal" - Ressenti a chaud</div>
            <p class="tax-body">
              La satisfaction est un <strong>ressenti emotionnel global</strong>, souvent collecte juste apres le sejour. C'est la mesure la plus repandue - et la plus limitee. Elle ne dit pas pourquoi un patient va mieux ou moins bien, ni ce qui a pose probleme precisement.
            </p>
            <ul class="tax-examples">
              <li>"Etes-vous satisfait de votre prise en charge ?"</li>
              <li>Note globale sur 10</li>
              <li>Recommanderiez-vous cet etablissement ?</li>
              <li>e-Satis est principalement une mesure de satisfaction</li>
            </ul>
          </div>

          <div class="tax-card">
            <div class="tax-number">04</div>
            <div class="tax-badge">Recit global</div>
            <div class="tax-title">Verbatim & recit qualitatif</div>
            <div class="tax-subtitle">Le "pourquoi" - Comprehension en profondeur</div>
            <p class="tax-body">
              Le recit libre du patient capture ce que les questionnaires fermes ne voient jamais : une douleur inattendue, une peur non exprimee, un detail du quotidien. C'est la <strong>matiere premiere</strong> la plus riche - mais la plus difficile a exploiter sans outil adapte.
            </p>
            <ul class="tax-examples">
              <li>"Depuis l'operation, je n'arrive plus a dormir sur le cote."</li>
              <li>"Ma femme m'aide encore pour m'habiller le matin."</li>
              <li>"Je n'avais pas compris qu'il fallait faire la kine aussi vite."</li>
              <li>Signaux precoces de complications non signalees</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="comparison">
      <div class="container">
        <div class="comp-header">
          <div>
            <div class="section-label">Tableau comparatif</div>
            <h2 class="section-title">Ce que chaque mesure<br>vous <em>apporte</em></h2>
          </div>
          <p class="section-intro">
            Comprendre les differences vous aide a choisir - et a expliquer a vos equipes medicales pourquoi chaque type de collecte a sa raison d'etre.
          </p>
        </div>

        <table class="comp-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Question centrale</th>
              <th>Quand collecter</th>
              <th>Usage principal</th>
              <th>Reglementaire HAS</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>PROMs</td>
              <td>Le soin a-t-il ameliore la vie du patient ?</td>
              <td>Avant l'acte + J+30, J+90, J+180</td>
              <td>Preuve d'efficacite clinique, IFAQ</td>
              <td>Critere 1.4-03</td>
            </tr>
            <tr>
              <td>PREMs</td>
              <td>Comment le patient a-t-il vecu sa prise en charge ?</td>
              <td>Pendant le sejour + J+2 a J+7</td>
              <td>Amelioration des processus, certification</td>
              <td>Critere 1.4-03</td>
            </tr>
            <tr>
              <td>Satisfaction</td>
              <td>Le patient est-il content ?</td>
              <td>A la sortie / J+2</td>
              <td>Communication, image de l'etablissement</td>
              <td>e-Satis (partiel)</td>
            </tr>
            <tr>
              <td>Recit global</td>
              <td>Que s'est-il vraiment passe dans la vie du patient ?</td>
              <td>Tout au long du parcours</td>
              <td>Detection precoce, amelioration continue</td>
              <td>Nourrit tous les indicateurs</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
defineEmits(['go-home'])
</script>

<style scoped>
.retours-view {
  background: #f8f6f6;
  color: #144e5a;
}

.container {
  width: min(1160px, 92%);
  margin: 0 auto;
}

section {
  padding: 72px 0;
}

.hero {
  padding-top: 54px;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 28px;
}

.hero-eyebrow {
  display: inline-flex;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(182, 207, 212, 0.5);
  font-size: 0.8rem;
  letter-spacing: 0.03em;
  font-weight: 700;
  text-transform: uppercase;
}

.hero-title {
  margin: 16px 0;
  font-size: clamp(2rem, 5vw, 3.6rem);
  line-height: 1.05;
  letter-spacing: -0.03em;
}

.hero-title em,
.section-title em {
  color: #ec6536;
  font-style: normal;
}

.hero-body {
  margin: 0 0 26px;
  line-height: 1.66;
  color: rgba(20, 78, 90, 0.72);
  max-width: 620px;
}

.hero-cta {
  display: inline-flex;
  text-decoration: none;
  border-radius: 12px;
  background: #ec6536;
  color: #ffffff;
  font-weight: 700;
  padding: 14px 20px;
}

.hero-right {
  display: grid;
  gap: 14px;
}

.hero-stat-card {
  background: #ffffff;
  border: 1px solid rgba(103, 170, 182, 0.4);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-shadow: 0 16px 40px rgba(20, 78, 90, 0.08);
}

.stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(182, 207, 212, 0.4);
  display: grid;
  place-items: center;
  font-size: 1.15rem;
}

.stat-text {
  display: grid;
  gap: 4px;
}

.stat-text strong {
  font-size: 0.97rem;
}

.stat-text span {
  font-size: 0.88rem;
  color: rgba(20, 78, 90, 0.72);
  line-height: 1.45;
}

.taxonomy {
  background: #ffffff;
}

.taxonomy-header,
.comp-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.section-label {
  display: inline-flex;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: rgba(20, 78, 90, 0.58);
}

.section-title {
  margin: 10px 0 0;
  font-size: clamp(1.7rem, 3.3vw, 2.9rem);
  line-height: 1.12;
  letter-spacing: -0.02em;
}

.section-intro {
  margin: 0;
  line-height: 1.68;
  color: rgba(20, 78, 90, 0.72);
}

.taxonomy-grid {
  margin-top: 28px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tax-card {
  background: #ffffff;
  border: 1px solid rgba(103, 170, 182, 0.4);
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 16px 40px rgba(20, 78, 90, 0.08);
}

.tax-number {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(182, 207, 212, 0.32);
  font-size: 0.86rem;
  font-weight: 800;
}

.tax-badge {
  margin-top: 10px;
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.tax-title {
  margin-top: 8px;
  font-weight: 800;
  font-size: 1.15rem;
  line-height: 1.35;
}

.tax-subtitle {
  margin-top: 6px;
  font-size: 0.9rem;
  color: rgba(20, 78, 90, 0.8);
  font-weight: 700;
}

.tax-body {
  margin: 13px 0 0;
  color: rgba(20, 78, 90, 0.72);
  line-height: 1.62;
}

.tax-examples {
  margin: 14px 0 0;
  padding-left: 18px;
  color: rgba(20, 78, 90, 0.85);
  line-height: 1.54;
}

.comparison {
  padding-top: 64px;
}

.comp-table {
  margin-top: 24px;
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border: 1px solid rgba(103, 170, 182, 0.4);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(20, 78, 90, 0.08);
}

.comp-table th,
.comp-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(103, 170, 182, 0.4);
  text-align: left;
  vertical-align: top;
  font-size: 0.92rem;
  line-height: 1.45;
}

.comp-table thead th {
  background: rgba(182, 207, 212, 0.3);
  font-size: 0.84rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

@media (max-width: 1024px) {
  .hero-grid,
  .taxonomy-header,
  .comp-header {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  section {
    padding: 52px 0;
  }

  .taxonomy-grid {
    grid-template-columns: 1fr;
  }

  .comp-table th,
  .comp-table td {
    padding: 11px 8px;
    font-size: 0.84rem;
  }
}
</style>
